import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { rule } from '../lib/fieldRules.js';
import { Seo } from '../components/Seo.jsx';
import { Button, Field, Input, Alert, Spinner } from '../components/ui.jsx';

const DEFAULT_NEXT = { creator: '/dashboard/creator/profile', brand: '/dashboard/brand/profile' };

/**
 * The last step of a first sign-in with Instagram (or a Facebook account with
 * no email): the address Affily writes to. It is confirmed by a link, like a
 * password sign-up's, and it can only start a new account — an existing one
 * is opened by signing in to it and adding Instagram on the Account page.
 */
export default function FinishSignup() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [pending, setPending] = useState(undefined);
  const [form, setForm] = useState({ email: '', name: '' });
  const [role, setRole] = useState('creator');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get('/auth/oauth/status')
      .then((s) => {
        setPending(s?.pending ?? null);
        if (s?.pending) {
          setForm((f) => ({ ...f, name: s.pending.name ?? s.pending.username ?? '' }));
          if (s.pending.role) setRole(s.pending.role);
        }
      })
      .catch(() => setPending(null));
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      const res = await api.post('/auth/oauth/finish', { ...form, role });
      await refresh();
      navigate(res.next ?? DEFAULT_NEXT[res.user.roles[0] ?? role] ?? '/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo title="One more step" noindex />
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <Link to="/" className="text-2xs font-semibold uppercase tracking-widest text-accent">
          Affily
        </Link>

        {pending === undefined ? (
          <Spinner className="mt-8" />
        ) : !pending ? (
          <>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">This sign-up has expired</h1>
            <p className="mt-2 text-sm text-ink-muted">
              It lasts 15 minutes.{' '}
              <Link to="/signup" className="text-accent hover:underline">
                Start again
              </Link>{' '}
              or{' '}
              <Link to="/login" className="text-accent hover:underline">
                sign in
              </Link>
              .
            </p>
          </>
        ) : (
          <>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">One more step: your email</h1>
            <p className="mt-1.5 text-sm text-ink-muted">
              {pending.label} {pending.username ? `(@${pending.username}) ` : ''}doesn’t share an email
              address, and Affily needs one for payouts and notices. We’ll send a link to confirm it.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <Alert>{error}</Alert>
              {!pending.role && (
                <fieldset>
                  <legend className="mb-1.5 text-sm font-medium">I’m joining as</legend>
                  <div className="grid grid-cols-2 gap-2">
                    {['creator', 'brand'].map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setRole(key)}
                        aria-pressed={role === key}
                        className={`rounded-lg border p-3 text-left text-sm font-medium ${
                          role === key ? 'border-accent bg-accent-soft' : 'border-rule-strong bg-paper-raised hover:bg-paper-sunken'
                        }`}
                      >
                        {key === 'creator' ? 'A creator' : 'A brand'}
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}
              <Field label="Name" required error={fieldErrors.name} constraint={rule.max(120)}>
                <Input id="finish-name" value={form.name} onChange={set('name')} autoComplete="name" required />
              </Field>
              <Field label="Email" required error={fieldErrors.email} constraint={rule.email()}>
                <Input
                  id="finish-email"
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  autoComplete="email"
                  required
                />
              </Field>
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? 'Creating account…' : 'Create my account'}
              </Button>
              <p className="text-xs text-ink-muted">
                Already on Affily with this email?{' '}
                <Link to="/login" className="text-accent hover:underline">
                  Sign in
                </Link>{' '}
                and add {pending.label} on your Account page.
              </p>
            </form>
          </>
        )}
      </div>
    </>
  );
}
